import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Car, MapPin, Clock, DollarSign, User, Phone, Calendar,
  Plus, RefreshCw, Settings, Filter, Eye, Check, X, AlertTriangle,
  Navigation, Shield, Star, Award, ChevronRight, FileText, Search,
  Activity, MessageCircle, Trash2, Edit, Briefcase, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { jobsAPI } from '../../services/api';
import DriverBadges from '../common/DriverBadges';
import MobileActionBar from '../common/MobileActionBar';
import { useAuth } from '../../context/AuthContext';
import DriverLiveMap from './DriverLiveMap';

// Ride Activity Log Component - shows activity for a specific ride
const RideActivityLog = ({ rideId }) => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await api.get(`/rides/${rideId}/activity`);
        setActivity(response.data.activity || []);
      } catch (error) {
        console.error('Failed to fetch ride activity:', error);
      }
      setLoading(false);
    };
    
    if (rideId && expanded) {
      fetchActivity();
    }
  }, [rideId, expanded]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getIcon = (action) => {
    if (action.includes('call')) return <Phone className="w-3 h-3 text-green-500" />;
    if (action.includes('text')) return <MessageCircle className="w-3 h-3 text-blue-500" />;
    if (action.includes('completed')) return <Check className="w-3 h-3 text-green-500" />;
    if (action.includes('cancelled')) return <X className="w-3 h-3 text-red-500" />;
    return <Activity className="w-3 h-3 text-[var(--primary)]" />;
  };

  return (
    <div className="border-t border-[var(--border-primary)] pt-3 mt-3">
      <button
        className="flex items-center justify-between w-full text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Activity Log
          {activity.length > 0 && <span className="text-xs bg-[var(--primary)] text-white px-1.5 rounded-full">{activity.length}</span>}
        </span>
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      
      {expanded && (
        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
          {loading ? (
            <div className="text-center py-2">
              <RefreshCw className="w-4 h-4 animate-spin mx-auto text-[var(--text-muted)]" />
            </div>
          ) : activity.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] text-center py-2">No activity recorded</p>
          ) : (
            activity.map((log) => (
              <div key={log.id} className="flex items-start gap-2 text-xs p-2 bg-[var(--background)] rounded">
                {getIcon(log.action)}
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--text-primary)] truncate">{log.description}</p>
                  <p className="text-[var(--text-muted)]">{formatTime(log.timestamp)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Status colors and labels
const STATUS_CONFIG = {
  requested: { label: 'Requested', color: 'bg-blue-100 text-blue-700', icon: Clock },
  pending_driver: { label: 'Finding Driver', color: 'bg-yellow-100 text-yellow-700', icon: Search },
  driver_assigned: { label: 'Driver Assigned', color: 'bg-purple-100 text-purple-700', icon: User },
  en_route_pickup: { label: 'En Route', color: 'bg-indigo-100 text-indigo-700', icon: Navigation },
  arrived_pickup: { label: 'Arrived', color: 'bg-cyan-100 text-cyan-700', icon: MapPin },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-700', icon: Car },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X }
};

const RidesTab = () => {
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverApplications, setDriverApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('rides');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [priceOverride, setPriceOverride] = useState('');
  const [isOverriding, setIsOverriding] = useState(false);
  const [editingRide, setEditingRide] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [assigningToMe, setAssigningToMe] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh driver stats every 30 seconds for live "Online Now" count
  useEffect(() => {
    const refreshDriverStats = async () => {
      try {
        const driverStatsRes = await api.get('/drivers/stats/overview').catch(() => ({ data: {} }));
        setDriverStats(driverStatsRes.data || {});
      } catch (error) {
        // Silent fail for background refresh
      }
    };
    
    const interval = setInterval(refreshDriverStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ridesRes, driversRes, appsRes, statsRes, driverStatsRes, pricingRes] = await Promise.all([
        api.get('/rides/').catch(e => { console.log('Rides fetch error:', e); return { data: [] }; }),
        api.get('/drivers/').catch(e => { console.log('Drivers fetch error:', e); return { data: [] }; }),
        api.get('/drivers/applications').catch(e => { console.log('Apps fetch error:', e); return { data: [] }; }),
        api.get('/rides/stats/overview').catch(e => { console.log('Ride stats error:', e); return { data: {} }; }),
        api.get('/drivers/stats/overview').catch(e => { console.log('Driver stats error:', e); return { data: {} }; }),
        api.get('/rides/pricing').catch(e => { console.log('Pricing fetch error:', e); return { data: {} }; })
      ]);
      
      console.log('RidesTab data:', { 
        rides: ridesRes.data, 
        drivers: driversRes.data,
        apps: appsRes.data,
        stats: statsRes.data, 
        driverStats: driverStatsRes.data 
      });
      
      setRides(Array.isArray(ridesRes.data) ? ridesRes.data : []);
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
      setDriverApplications(Array.isArray(appsRes.data) ? appsRes.data : []);
      setStats(statsRes.data || {});
      setDriverStats(driverStatsRes.data || {});
      setPricing(pricingRes.data || {});
    } catch (error) {
      console.error('Error fetching rides data:', error);
      toast.error('Failed to load rides data');
    }
    setLoading(false);
  };

  const handleAssignDriver = async (rideId, driverId) => {
    try {
      await api.put(`/rides/${rideId}/assign?driver_id=${driverId}`);
      toast.success('Driver assigned successfully');
      fetchData();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  // Handle owner self-assignment to a ride
  const handleAssignToMe = async (ride) => {
    if (!user?.id) {
      toast.error('Unable to identify current user');
      return;
    }
    
    try {
      setAssigningToMe(ride.id);
      await api.put(`/rides/${ride.id}/assign?driver_id=${user.id}`);
      toast.success(`You've been assigned to ride ${ride.id.slice(0, 8)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigningToMe(null);
    }
  };

  const handleUpdateRideStatus = async (rideId, status) => {
    try {
      await api.put(`/rides/${rideId}/status?status=${status}`);
      toast.success('Ride status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCancelRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to cancel this ride?')) return;
    try {
      await api.put(`/rides/${rideId}/cancel`);
      toast.success('Ride cancelled');
      fetchData();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const handleDeleteRide = async (rideId, reference) => {
    if (!window.confirm(`Are you sure you want to permanently delete ride ${reference}? This cannot be undone.`)) return;
    try {
      await api.delete(`/rides/${rideId}`);
      toast.success(`Ride ${reference} deleted`);
      fetchData();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ride');
    }
  };

  const handleApproveApplication = async (appId) => {
    try {
      await api.put(`/drivers/applications/${appId}/status?status=approved`);
      toast.success('Application approved! Driver account created.');
      fetchData();
      setSelectedApplication(null);
    } catch (error) {
      toast.error('Failed to approve application');
    }
  };

  const handleRejectApplication = async (appId, reason) => {
    try {
      await api.put(`/drivers/applications/${appId}/status?status=rejected&notes=${reason || 'Not approved'}`);
      toast.success('Application rejected');
      fetchData();
      setSelectedApplication(null);
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const handleUpdatePricing = async (newPricing) => {
    try {
      await api.put('/rides/pricing', newPricing);
      toast.success('Pricing updated');
      setPricing(newPricing);
      setShowPricingModal(false);
    } catch (error) {
      toast.error('Failed to update pricing');
    }
  };

  const handlePriceOverride = async (rideId, newPrice) => {
    if (!newPrice || isNaN(parseFloat(newPrice))) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setIsOverriding(true);
    try {
      await api.put(`/rides/${rideId}/price-override`, { 
        override_price: parseFloat(newPrice) 
      });
      toast.success('Price updated successfully');
      
      // Update local state
      setRides(prev => prev.map(r => 
        r.id === rideId 
          ? { ...r, estimated_fare: parseFloat(newPrice), price_overridden: true }
          : r
      ));
      
      if (selectedRide?.id === rideId) {
        setSelectedRide(prev => ({ 
          ...prev, 
          estimated_fare: parseFloat(newPrice),
          price_overridden: true 
        }));
      }
      
      setPriceOverride('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update price');
    }
    setIsOverriding(false);
  };

  const handleEditRide = (ride) => {
    setEditingRide(ride);
    setEditForm({
      pickup_address: ride.pickup_location?.address || '',
      pickup_city: ride.pickup_location?.city || '',
      pickup_zip: ride.pickup_location?.zip_code || '',
      dropoff_address: ride.dropoff_location?.address || '',
      dropoff_city: ride.dropoff_location?.city || '',
      dropoff_zip: ride.dropoff_location?.zip_code || '',
      scheduled_date: ride.scheduled_pickup?.split('T')[0] || '',
      scheduled_time: ride.scheduled_pickup?.split('T')[1]?.slice(0,5) || '',
      estimated_fare: ride.estimated_fare || 0,
      passenger_count: ride.passenger_count || 1,
      special_instructions: ride.special_instructions || '',
      vehicle_type: ride.vehicle_type || 'sedan'
    });
  };

  const handleSaveRideEdit = async () => {
    if (!editingRide) return;
    
    try {
      const updateData = {
        pickup_location: {
          address: editForm.pickup_address,
          city: editForm.pickup_city,
          zip_code: editForm.pickup_zip,
          state: 'NC'
        },
        dropoff_location: {
          address: editForm.dropoff_address,
          city: editForm.dropoff_city,
          zip_code: editForm.dropoff_zip,
          state: 'NC'
        },
        scheduled_pickup: editForm.scheduled_date && editForm.scheduled_time 
          ? `${editForm.scheduled_date}T${editForm.scheduled_time}:00`
          : null,
        estimated_fare: parseFloat(editForm.estimated_fare),
        passenger_count: parseInt(editForm.passenger_count),
        special_instructions: editForm.special_instructions,
        vehicle_type: editForm.vehicle_type
      };
      
      await api.put(`/rides/${editingRide.id}`, updateData);
      toast.success('Ride updated successfully');
      fetchData();
      setEditingRide(null);
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ride');
    }
  };

  // Post ride to job board for drivers to claim
  const handlePostRideToJobBoard = async (ride) => {
    try {
      // Create a job for this ride
      const jobData = {
        service_type: 'ride',
        service_name: `Ride: ${ride.pickup_location?.city || 'Pickup'} to ${ride.dropoff_location?.city || 'Dropoff'}`,
        scheduled_date: ride.scheduled_pickup?.split('T')[0] || new Date().toISOString().split('T')[0],
        scheduled_time: ride.scheduled_pickup?.split('T')[1]?.slice(0, 5) || '09:00',
        address: ride.pickup_location?.address || '',
        city: ride.pickup_location?.city || '',
        zip_code: ride.pickup_location?.zip_code || '',
        notes: `Ride for ${ride.customer_name || 'Customer'}\nPickup: ${ride.pickup_location?.address || ''}\nDropoff: ${ride.dropoff_location?.address || ''}\nPassengers: ${ride.passenger_count || 1}\nFare: $${ride.estimated_fare?.toFixed(2) || '0.00'}`,
        estimated_duration: 60,
        estimated_pay: ride.estimated_fare ? ride.estimated_fare * 0.75 : 15, // Driver gets 75%
        booking_id: ride.id,
        job_posted: true
      };
      
      await jobsAPI.create(jobData);
      toast.success('Ride posted to job board! Drivers can now claim this ride.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post ride to job board');
    }
  };

  // Get pending rides that need assignment
  const pendingRides = rides.filter(r => r.status === 'requested');

  const filteredRides = statusFilter 
    ? rides.filter(r => r.status === statusFilter)
    : rides;

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading rides data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rides-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Car className="w-6 h-6 text-[var(--primary)]" />
            Rides & Transportation
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage ride requests, drivers, and transportation services
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPricingModal(true)} data-testid="pricing-settings-btn">
            <Settings className="w-4 h-4 mr-2" /> Pricing
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="card p-4 border-l-4 border-blue-400">
          <p className="text-2xl font-bold text-[#D4AF37]">{stats?.total_rides || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Total Rides</p>
        </div>
        <div className="card p-4 border-l-4 border-yellow-400">
          <p className="text-2xl font-bold text-[var(--warning)]">{stats?.pending_rides || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Pending</p>
        </div>
        <div className="card p-4 border-l-4 border-green-400">
          <p className="text-2xl font-bold text-[var(--success)]">{stats?.completed_rides || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Completed</p>
        </div>
        <div className="card p-4 border-l-4 border-emerald-500 bg-gradient-to-r from-emerald-500/10 to-transparent">
          <p className="text-2xl font-bold text-emerald-500">${(stats?.estimated_revenue || 0).toFixed(2)}</p>
          <p className="text-xs text-[var(--text-muted)]">Ride Revenue</p>
        </div>
        <div className="card p-4 border-l-4 border-purple-400">
          <p className="text-2xl font-bold text-purple-600">{driverStats?.total_drivers || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Total Drivers</p>
        </div>
        {/* Online Drivers - Live Status Card */}
        <div className="card p-4 border-l-4 border-green-500 bg-gradient-to-r from-green-500/10 to-transparent relative overflow-hidden" data-testid="online-drivers-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-green-500">{driverStats?.available_now || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Online Now</p>
            </div>
            {(driverStats?.available_now || 0) > 0 && (
              <div className="relative">
                <span className="flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="card p-4 border-l-4 border-orange-400">
          <p className="text-2xl font-bold text-orange-600">{driverStats?.pending_applications || 0}</p>
          <p className="text-xs text-[var(--text-muted)]">Pending Apps</p>
        </div>
      </div>

      {/* Driver Live Map */}
      <DriverLiveMap onSelectDriver={(driver) => {
        setActiveSubTab('drivers');
        // Could add logic to highlight selected driver
      }} />

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-[var(--border-primary)] pb-2">
        {[
          { id: 'rides', label: 'Ride Requests', icon: Car },
          { id: 'drivers', label: 'Drivers', icon: User },
          { id: 'applications', label: 'Applications', icon: FileText, badge: driverStats?.pending_applications }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeSubTab === tab.id 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-muted)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid={`rides-subtab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Rides List */}
      {activeSubTab === 'rides' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={statusFilter === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('')}
            >
              All ({rides.length})
            </Button>
            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
              const count = rides.filter(r => r.status === status).length;
              if (count === 0) return null;
              return (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                >
                  {config.label} ({count})
                </Button>
              );
            })}
          </div>

          {/* Rides Grid */}
          {filteredRides.length === 0 ? (
            <div className="card p-12 text-center">
              <Car className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
              <h3 className="text-lg font-semibold mb-2">No Ride Requests</h3>
              <p className="text-[var(--text-muted)]">
                {statusFilter ? `No rides with status "${STATUS_CONFIG[statusFilter]?.label}"` : 'No ride requests yet'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredRides.map(ride => {
                const statusConfig = STATUS_CONFIG[ride.status] || STATUS_CONFIG.requested;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <motion.div
                    key={ride.id}
                    whileHover={{ y: -2 }}
                    className="card p-4 cursor-pointer hover:border-[var(--primary)]"
                    onClick={() => setSelectedRide(ride)}
                    data-testid={`ride-card-${ride.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="font-mono text-sm text-[var(--primary)]">{ride.reference}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {statusConfig.label}
                      </span>
                    </div>
                    
                    <h4 className="font-semibold">{ride.customer_name}</h4>
                    
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5"></div>
                        <span className="text-[var(--text-secondary)] line-clamp-1">
                          {ride.pickup_location?.address}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5"></div>
                        <span className="text-[var(--text-secondary)] line-clamp-1">
                          {ride.dropoff_location?.address}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-[var(--text-muted)]">
                        <Calendar className="w-4 h-4" />
                        {new Date(ride.pickup_datetime).toLocaleDateString()}
                      </span>
                      <span className="font-semibold text-[var(--primary)]">
                        ${ride.estimated_fare?.toFixed(2) || '—'}
                      </span>
                    </div>
                    
                    {ride.driver_name && (
                      <div className="mt-2 pt-2 border-t border-[var(--border-primary)] text-sm">
                        <span className="text-[var(--text-muted)]">Driver: </span>
                        <span className="font-medium">{ride.driver_name}</span>
                      </div>
                    )}
                    
                    {/* Quick Actions for pending rides */}
                    {ride.status === 'requested' && (
                      <div className="mt-3 pt-3 border-t border-[var(--border-primary)] space-y-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs"
                            onClick={(e) => { e.stopPropagation(); handlePostRideToJobBoard(ride); }}
                            data-testid={`post-job-${ride.id}`}
                          >
                            <Briefcase className="w-3 h-3 mr-1" /> Post to Job Board
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 text-xs bg-green-600 hover:bg-green-700"
                            onClick={(e) => { e.stopPropagation(); setSelectedRide(ride); }}
                            data-testid={`assign-driver-${ride.id}`}
                          >
                            <User className="w-3 h-3 mr-1" /> Assign
                          </Button>
                          <Button
                            size="sm"
                            className="text-xs bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                            onClick={(e) => { e.stopPropagation(); handleAssignToMe(ride); }}
                            disabled={assigningToMe === ride.id}
                            data-testid={`assign-to-me-${ride.id}`}
                          >
                            {assigningToMe === ride.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <User className="w-3 h-3 mr-1" /> Me
                              </>
                            )}
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-xs text-red-500 border-red-500/30 hover:bg-red-500/10"
                          onClick={(e) => { e.stopPropagation(); handleCancelRide(ride.id); }}
                          data-testid={`cancel-ride-${ride.id}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Cancel Ride
                        </Button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Drivers List */}
      {activeSubTab === 'drivers' && (
        <div className="space-y-4">
          {drivers.length === 0 ? (
            <div className="card p-12 text-center">
              <User className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
              <h3 className="text-lg font-semibold mb-2">No Drivers Yet</h3>
              <p className="text-[var(--text-muted)]">
                Approve driver applications to add drivers
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {drivers.map(driver => (
                <div key={driver.id} className="card p-4" data-testid={`driver-card-${driver.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center">
                      <User className="w-6 h-6 text-black" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{driver.display_name}</h4>
                      <p className="text-sm text-[var(--text-muted)]">{driver.vehicle_description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      driver.is_available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {driver.is_available ? 'Available' : 'Offline'}
                    </span>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                    <div>
                      <p className="font-semibold text-[var(--primary)]">{driver.total_rides || 0}</p>
                      <p className="text-xs text-[var(--text-muted)]">Rides</p>
                    </div>
                    <div>
                      <p className="font-semibold text-[#D4AF37]">{driver.rating?.toFixed(1) || '5.0'}</p>
                      <p className="text-xs text-[var(--text-muted)]">Rating</p>
                    </div>
                    <div>
                      <p className="font-semibold text-teal-600">{driver.on_time_percentage?.toFixed(0) || 100}%</p>
                      <p className="text-xs text-[var(--text-muted)]">On Time</p>
                    </div>
                  </div>
                  
                  {driver.badges?.length > 0 && (
                    <div className="mt-3">
                      <DriverBadges badges={driver.badges} size="sm" maxDisplay={4} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Driver Applications */}
      {activeSubTab === 'applications' && (
        <div className="space-y-4">
          {driverApplications.length === 0 ? (
            <div className="card p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)]" />
              <h3 className="text-lg font-semibold mb-2">No Applications</h3>
              <p className="text-[var(--text-muted)]">
                No pending driver applications
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {driverApplications.map(app => (
                <div 
                  key={app.id} 
                  className="card p-4 cursor-pointer hover:border-[var(--primary)]"
                  onClick={() => setSelectedApplication(app)}
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{app.applicant_name}</h4>
                        <p className="text-sm text-[var(--text-muted)]">{app.applicant_email}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          Applied: {new Date(app.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        app.status === 'approved' ? 'bg-green-100 text-green-700' :
                        app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {app.status.toUpperCase()}
                      </span>
                      <p className="text-sm mt-1">
                        {app.vehicle_info?.year} {app.vehicle_info?.make} {app.vehicle_info?.model}
                      </p>
                    </div>
                  </div>
                  
                  {app.status === 'pending' && (
                    <div className="mt-4 flex gap-2 justify-end">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleRejectApplication(app.id); }}
                      >
                        Reject
                      </Button>
                      <Button 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleApproveApplication(app.id); }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ride Details Modal */}
      {selectedRide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border-primary)]">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-sm text-[var(--primary)]">{selectedRide.reference}</span>
                  <h3 className="text-xl font-bold mt-1">{selectedRide.customer_name}</h3>
                </div>
                <Button variant="ghost" onClick={() => setSelectedRide(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="font-medium">Status:</span>
                <span className={`px-3 py-1 rounded-full ${STATUS_CONFIG[selectedRide.status]?.color || 'bg-gray-100'}`}>
                  {STATUS_CONFIG[selectedRide.status]?.label || selectedRide.status}
                </span>
              </div>
              
              {/* Route */}
              <div className="card p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Pickup</p>
                    <p className="font-medium">{selectedRide.pickup_location?.address}</p>
                  </div>
                </div>
                <div className="ml-3 border-l-2 border-dashed border-gray-300 h-4"></div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Dropoff</p>
                    <p className="font-medium">{selectedRide.dropoff_location?.address}</p>
                  </div>
                </div>
              </div>
              
              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[var(--text-muted)]">Pickup Time</p>
                  <p className="font-medium">{new Date(selectedRide.pickup_datetime).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Passengers</p>
                  <p className="font-medium">{selectedRide.passenger_count}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Vehicle Type</p>
                  <p className="font-medium capitalize">{selectedRide.preferred_vehicle_type || 'Any'}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Payment Method</p>
                  <p className="font-medium capitalize">{selectedRide.payment_method || 'Card'}</p>
                </div>
              </div>
              
              {/* Price Override Section */}
              <div className="card p-4 border-2 border-[var(--primary)]/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[var(--primary)]" />
                    Ride Fare
                  </h4>
                  {selectedRide.price_overridden && (
                    <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                      Price Overridden
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-xs text-[var(--text-muted)]">Current Fare</p>
                    <p className="text-2xl font-bold text-[var(--primary)]">
                      ${selectedRide.estimated_fare?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  {selectedRide.original_fare && selectedRide.original_fare !== selectedRide.estimated_fare && (
                    <div>
                      <p className="text-xs text-[var(--text-muted)]">Original</p>
                      <p className="text-lg text-[var(--text-muted)] line-through">
                        ${selectedRide.original_fare?.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Price Override Input - only show for non-completed rides */}
                {!['completed', 'cancelled'].includes(selectedRide.status) && (
                  <div className="pt-3 border-t border-[var(--border-primary)]">
                    <p className="text-sm text-[var(--text-muted)] mb-2">Override Price</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={selectedRide.estimated_fare?.toFixed(2) || '0.00'}
                          value={priceOverride}
                          onChange={(e) => setPriceOverride(e.target.value)}
                          className="pl-7"
                          data-testid="price-override-input"
                        />
                      </div>
                      <Button
                        onClick={() => handlePriceOverride(selectedRide.id, priceOverride)}
                        disabled={isOverriding || !priceOverride}
                        className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]"
                        data-testid="apply-price-override-btn"
                      >
                        {isOverriding ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Apply
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mt-2">
                      Enter a new price to override the calculated fare before confirming the ride
                    </p>
                  </div>
                )}
              </div>
              
              {/* Assign Driver */}
              {selectedRide.status === 'requested' && (
                <div className="pt-4 border-t border-[var(--border-primary)]">
                  <h4 className="font-semibold mb-3">Assign Driver</h4>
                  
                  {/* Quick Assign to Me Button */}
                  <Button
                    className="w-full mb-4 bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                    onClick={() => handleAssignToMe(selectedRide)}
                    disabled={assigningToMe === selectedRide.id}
                    data-testid="assign-to-me-modal-btn"
                  >
                    {assigningToMe === selectedRide.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <User className="w-4 h-4 mr-2" />
                    )}
                    Assign to Me (Owner)
                  </Button>
                  
                  {drivers.length === 0 ? (
                    <p className="text-[var(--text-muted)] text-sm">No drivers available. Add drivers from the Workers tab.</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Available drivers first, then offline */}
                      {[...drivers.filter(d => d.is_available), ...drivers.filter(d => !d.is_available)].map(driver => (
                        <div 
                          key={driver.id}
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                            driver.is_available 
                              ? 'hover:bg-green-50 border-green-200' 
                              : 'hover:bg-[var(--background-subtle)] opacity-75'
                          }`}
                          onClick={() => handleAssignDriver(selectedRide.id, driver.worker_id)}
                          data-testid={`assign-driver-${driver.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              driver.is_available 
                                ? 'bg-gradient-to-br from-green-400 to-green-600' 
                                : 'bg-gradient-to-br from-gray-300 to-gray-500'
                            }`}>
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium">{driver.display_name}</p>
                              <p className="text-xs text-[var(--text-muted)]">{driver.vehicle_description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-[#D4AF37]" />
                            <span>{driver.rating?.toFixed(1) || '5.0'}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              driver.is_available 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {driver.is_available ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Ride Actions */}
              <div className="pt-4 border-t border-[var(--border-primary)] space-y-3">
                <h4 className="font-semibold">Actions</h4>
                
                {/* Edit button - for non-completed rides */}
                <div className="flex flex-wrap gap-2">
                  {!['completed', 'cancelled'].includes(selectedRide.status) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10"
                      onClick={() => handleEditRide(selectedRide)}
                      data-testid="edit-ride-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit Ride
                    </Button>
                  )}
                  
                  {/* Status update buttons based on current status */}
                  {selectedRide.status === 'driver_assigned' && (
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleUpdateRideStatus(selectedRide.id, 'en_route_pickup')}
                    >
                      <Navigation className="w-4 h-4 mr-1" /> Mark En Route
                    </Button>
                  )}
                  
                  {selectedRide.status === 'en_route_pickup' && (
                    <Button 
                      size="sm" 
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => handleUpdateRideStatus(selectedRide.id, 'arrived_pickup')}
                    >
                      <MapPin className="w-4 h-4 mr-1" /> Mark Arrived
                    </Button>
                  )}
                  
                  {selectedRide.status === 'arrived_pickup' && (
                    <Button 
                      size="sm" 
                      className="bg-orange-600 hover:bg-orange-700"
                      onClick={() => handleUpdateRideStatus(selectedRide.id, 'in_progress')}
                    >
                      <Car className="w-4 h-4 mr-1" /> Start Ride
                    </Button>
                  )}
                  
                  {selectedRide.status === 'in_progress' && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleUpdateRideStatus(selectedRide.id, 'completed')}
                    >
                      <Check className="w-4 h-4 mr-1" /> Complete Ride
                    </Button>
                  )}
                  
                  {/* Cancel button - available for non-completed rides */}
                  {!['completed', 'cancelled'].includes(selectedRide.status) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                      onClick={() => handleCancelRide(selectedRide.id)}
                      data-testid="cancel-ride-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel Ride
                    </Button>
                  )}
                  
                  {/* Delete button - only for completed or cancelled rides */}
                  {['completed', 'cancelled'].includes(selectedRide.status) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteRide(selectedRide.id, selectedRide.reference)}
                      data-testid="delete-ride-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Ride
                    </Button>
                  )}
                </div>
                
                {/* Driver info if assigned */}
                {selectedRide.driver_name && (
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                    <p className="text-sm text-[var(--text-muted)]">Assigned Driver</p>
                    <p className="font-semibold">{selectedRide.driver_name}</p>
                  </div>
                )}
                
                {/* GPS Info */}
                {selectedRide.start_gps && (
                  <div className="p-3 bg-green-50 rounded-lg text-sm">
                    <p className="text-green-700 font-medium">Ride Started</p>
                    <p className="text-green-600 text-xs">
                      GPS: {selectedRide.start_gps.lat?.toFixed(5)}, {selectedRide.start_gps.lng?.toFixed(5)}
                    </p>
                  </div>
                )}
                
                {selectedRide.end_gps && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <p className="text-blue-700 font-medium">Ride Completed</p>
                    <p className="text-blue-600 text-xs">
                      GPS Verified: {selectedRide.end_gps.verified ? 'Yes' : 'No'}
                      {selectedRide.end_gps.distance_feet && ` (${selectedRide.end_gps.distance_feet} ft from dropoff)`}
                    </p>
                  </div>
                )}
                
                {/* Activity Log Section */}
                <RideActivityLog rideId={selectedRide.id} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ride Modal */}
      {editingRide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border-primary)]">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Edit className="w-5 h-5 text-[var(--primary)]" />
                  Edit Ride - {editingRide.reference}
                </h3>
                <Button variant="ghost" onClick={() => setEditingRide(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Pickup Location */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-500" /> Pickup Location
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Address</label>
                    <Input
                      value={editForm.pickup_address}
                      onChange={e => setEditForm(prev => ({ ...prev, pickup_address: e.target.value }))}
                      placeholder="Street address"
                      data-testid="edit-pickup-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">City</label>
                    <Input
                      value={editForm.pickup_city}
                      onChange={e => setEditForm(prev => ({ ...prev, pickup_city: e.target.value }))}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">ZIP Code</label>
                    <Input
                      value={editForm.pickup_zip}
                      onChange={e => setEditForm(prev => ({ ...prev, pickup_zip: e.target.value }))}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>
              
              {/* Dropoff Location */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-500" /> Dropoff Location
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Address</label>
                    <Input
                      value={editForm.dropoff_address}
                      onChange={e => setEditForm(prev => ({ ...prev, dropoff_address: e.target.value }))}
                      placeholder="Street address"
                      data-testid="edit-dropoff-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">City</label>
                    <Input
                      value={editForm.dropoff_city}
                      onChange={e => setEditForm(prev => ({ ...prev, dropoff_city: e.target.value }))}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">ZIP Code</label>
                    <Input
                      value={editForm.dropoff_zip}
                      onChange={e => setEditForm(prev => ({ ...prev, dropoff_zip: e.target.value }))}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>
              
              {/* Schedule & Pricing */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" /> Schedule & Pricing
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Date</label>
                    <Input
                      type="date"
                      value={editForm.scheduled_date}
                      onChange={e => setEditForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
                      data-testid="edit-ride-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Time</label>
                    <Input
                      type="time"
                      value={editForm.scheduled_time}
                      onChange={e => setEditForm(prev => ({ ...prev, scheduled_time: e.target.value }))}
                      data-testid="edit-ride-time"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Fare ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.estimated_fare}
                      onChange={e => setEditForm(prev => ({ ...prev, estimated_fare: e.target.value }))}
                      data-testid="edit-ride-fare"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Passengers</label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={editForm.passenger_count}
                      onChange={e => setEditForm(prev => ({ ...prev, passenger_count: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Vehicle Type & Instructions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">Vehicle Type</label>
                  <select
                    className="w-full p-2 rounded-lg border border-[var(--border-primary)] bg-[var(--background)]"
                    value={editForm.vehicle_type}
                    onChange={e => setEditForm(prev => ({ ...prev, vehicle_type: e.target.value }))}
                  >
                    <option value="sedan">Sedan (1-4 passengers)</option>
                    <option value="suv">SUV (1-6 passengers)</option>
                    <option value="van">Van (1-7 passengers)</option>
                    <option value="wheelchair">Wheelchair Accessible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">Special Instructions</label>
                  <textarea
                    className="w-full p-2 rounded-lg border border-[var(--border-primary)] bg-[var(--background)] min-h-[80px]"
                    value={editForm.special_instructions}
                    onChange={e => setEditForm(prev => ({ ...prev, special_instructions: e.target.value }))}
                    placeholder="Any special requirements..."
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-[var(--border-primary)] flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingRide(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRideEdit} data-testid="save-ride-edit-btn">
                <Check className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricingModal && pricing && (
        <PricingModal
          pricing={pricing}
          onClose={() => setShowPricingModal(false)}
          onSave={handleUpdatePricing}
        />
      )}

      {/* Mobile Action Bar */}
      <MobileActionBar
        actions={[
          ...(pendingRides.length > 0 ? [{
            id: 'approve-rides',
            label: `Review ${pendingRides.length} Pending`,
            icon: 'Check',
            variant: 'warning',
            onClick: () => pendingRides.length > 0 && setSelectedRide(pendingRides[0])
          }] : []),
          {
            id: 'pricing',
            label: 'Pricing Settings',
            icon: 'Edit',
            onClick: () => setShowPricingModal(true)
          }
        ]}
      />
    </div>
  );
};

// Pricing Configuration Modal
const PricingModal = ({ pricing, onClose, onSave }) => {
  const [formData, setFormData] = useState(pricing);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--background-paper)] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[var(--border-primary)]">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">Ride Pricing Configuration</h3>
            <Button variant="ghost" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Base Fare ($)</label>
              <Input
                type="number"
                step="0.50"
                value={formData.base_fare}
                onChange={(e) => handleChange('base_fare', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Per Mile Rate ($)</label>
              <Input
                type="number"
                step="0.25"
                value={formData.per_mile_rate}
                onChange={(e) => handleChange('per_mile_rate', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Minimum Fare ($)</label>
              <Input
                type="number"
                step="1.00"
                value={formData.minimum_fare}
                onChange={(e) => handleChange('minimum_fare', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Driver Pay %</label>
              <Input
                type="number"
                step="1"
                min="50"
                max="90"
                value={formData.driver_pay_percentage}
                onChange={(e) => handleChange('driver_pay_percentage', e.target.value)}
              />
            </div>
          </div>
          
          <h4 className="font-semibold pt-4">Vehicle Multipliers</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs">SUV</label>
              <Input
                type="number"
                step="0.05"
                value={formData.suv_multiplier}
                onChange={(e) => handleChange('suv_multiplier', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs">Van</label>
              <Input
                type="number"
                step="0.05"
                value={formData.van_multiplier}
                onChange={(e) => handleChange('van_multiplier', e.target.value)}
              />
            </div>
          </div>
          
          <h4 className="font-semibold pt-4">Additional Fees</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs">Extra Stop Fee ($)</label>
              <Input
                type="number"
                step="1.00"
                value={formData.extra_stop_fee}
                onChange={(e) => handleChange('extra_stop_fee', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs">Assistance Fee ($)</label>
              <Input
                type="number"
                step="1.00"
                value={formData.assistance_fee}
                onChange={(e) => handleChange('assistance_fee', e.target.value)}
              />
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-[var(--border-primary)] flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Save Pricing</Button>
        </div>
      </div>
    </div>
  );
};

export default RidesTab;
